import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Container,
  Skeleton,
  Alert,
  Button,
  Tooltip,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip, Cell } from 'recharts';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Lock, CheckCircleOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { fmtDateShort, tzParam } from '../utils/dateFormat';

// Country name → ISO 2-letter code for flag display
const COUNTRY_TO_CODE = {
  India: 'IN', 'United Arab Emirates': 'AE', UAE: 'AE', Singapore: 'SG',
  'United States': 'US', USA: 'US', 'United Kingdom': 'GB', UK: 'GB',
  Germany: 'DE', France: 'FR', Japan: 'JP', Australia: 'AU', Canada: 'CA',
  Netherlands: 'NL', Brazil: 'BR', Mexico: 'MX', Unknown: 'XX', unknown: 'XX',
};

function getFlagEmoji(countryCode) {
  if (!countryCode || countryCode.length !== 2) return '🌐';
  const code = countryCode.toUpperCase();
  return String.fromCodePoint(...[...code].map(c => 0x1f1e6 - 65 + c.charCodeAt(0)));
}

const PRIMARY = '#00A7E3';
const ACCENT = '#102E5C';

const DUMMY_STATS = { monthlyViews: 1247, uniqueVisitors: 893, contactSaves: 156 };
const DUMMY_CHART = Array.from({ length: 12 }, (_, i) => {
  const d = new Date(); d.setDate(d.getDate() - (11 - i));
  return { name: fmtDateShort(d), views: Math.floor(Math.random() * 80) + 30 };
});
const DUMMY_LOCATIONS = [
  { city: 'Mumbai', country: 'India', percentage: 34 },
  { city: 'New York', country: 'United States', percentage: 22 },
  { city: 'London', country: 'United Kingdom', percentage: 18 },
  { city: 'Dubai', country: 'United Arab Emirates', percentage: 14 },
  { city: 'Singapore', country: 'Singapore', percentage: 12 },
];

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDark = theme.palette.mode === 'dark';
  const isFreePlan = user?.role !== 'admin' && (!user?.plan || user.plan === 'free') && user?.plan !== 'premium';
  const [stats, setStats] = useState({
    monthlyViews: 0,
    uniqueVisitors: 0,
    contactSaves: 0,
  });
  const [chartData, setChartData] = useState({ labels: [], data: [] });
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsRes, chartRes, locationsRes] = await Promise.all([
        api.get('/cards/dashboard-stats'),
        api.get(`/cards/dashboard-chart?${tzParam}`),
        api.get(`/analytics/dashboard-locations?${tzParam}`).catch(() => ({ data: { locations: [] } })),
      ]);
      if (statsRes.data?.success && statsRes.data?.stats) {
        const s = statsRes.data.stats;
        setStats({
          monthlyViews: s.monthlyViews ?? 0,
          uniqueVisitors: s.uniqueVisitors ?? 0,
          contactSaves: s.contactSaves ?? 0,
        });
      }
      if (chartRes.data?.success && chartRes.data?.labels?.length) {
        setChartData({
          labels: chartRes.data.labels,
          data: chartRes.data.labels.map((label, i) => ({
            name: label,
            views: chartRes.data.data[i] ?? 0,
          })),
        });
      } else {
        setChartData({
          labels: [],
          data: Array.from({ length: 12 }, (_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - (11 - i));
            return { name: fmtDateShort(d), views: 0 };
          }),
        });
      }
      if (locationsRes.data?.success && locationsRes.data?.locations?.length) {
        setLocations(locationsRes.data.locations);
      } else {
        setLocations([]);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const kpiColors = {
    views: '#00A7E3',
    unique: theme.palette.success.main,
    saves: theme.palette.warning.main,
  };

  if (error) {
    return (
      <Container maxWidth={false} sx={{ py: 4, width: '100%' }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchDashboardData} startIcon={<RefreshIcon />}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  const displayStats = isFreePlan ? DUMMY_STATS : stats;
  const displayChart = isFreePlan ? DUMMY_CHART : chartData.data;
  const displayLocations = isFreePlan ? DUMMY_LOCATIONS : locations;
  /** Recharts axis labels need strong contrast; secondary alone reads as near-black on some dark SVG renders */
  const chartAxisTickColor = isDark ? theme.palette.text.primary : theme.palette.text.secondary;

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3 }, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Your Analytics Dashboard
        </Typography>
        <Tooltip title="Dashboard data is current; refresh the page to update." arrow placement="left">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', animation: 'pulse 2s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>Live</Typography>
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {isFreePlan && <DashboardPaywall onUpgrade={() => navigate('/subscription')} />}

        <Box sx={isFreePlan ? { filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none' } : undefined}>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {[
              { label: 'TOTAL VIEWS', value: displayStats.monthlyViews, color: kpiColors.views },
              { label: 'UNIQUE', value: displayStats.uniqueVisitors, color: kpiColors.unique },
              { label: 'SAVES', value: displayStats.contactSaves, color: kpiColors.saves },
            ].map(kpi => (
              <Grid item xs={12} sm={4} key={kpi.label}>
                <Card sx={{ bgcolor: isDark ? 'grey.800' : 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, height: '100%' }}>
                  <CardContent sx={{ py: 3, px: 3 }}>
                    {loading && !isFreePlan ? <Skeleton variant="text" width="50%" height={44} /> : (
                      <Typography variant="h4" sx={{ fontWeight: 700, color: kpi.color }}>{kpi.value.toLocaleString()}</Typography>
                    )}
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 0.5, display: 'block', mt: 0.5, fontSize: '0.7rem' }}>{kpi.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card sx={{ mb: 3, bgcolor: isDark ? 'grey.800' : 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <CardContent sx={{ py: 3, px: 3 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 0.5, display: 'block', mb: 2, fontSize: '0.7rem' }}>VIEWS — LAST 12 DAYS</Typography>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={displayChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fill: chartAxisTickColor, fontSize: 12 }} axisLine={{ stroke: theme.palette.divider }} tickLine={false} />
                    <YAxis tick={{ fill: chartAxisTickColor, fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                        color: theme.palette.text.primary,
                      }}
                      labelStyle={{ color: theme.palette.text.primary }}
                      itemStyle={{ color: theme.palette.text.primary }}
                      formatter={(value) => [value, 'Views']}
                    />
                    <Bar dataKey="views" radius={[2, 2, 0, 0]} maxBarSize={56}>
                      {displayChart?.map((_, index) => {
                        const len = displayChart.length;
                        const isHighlight = len >= 3 && index >= len - 3;
                        return <Cell key={index} fill={isHighlight ? '#00A7E3' : isDark ? '#475569' : '#94a3b8'} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3, bgcolor: isDark ? 'grey.800' : 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <CardContent sx={{ py: 3, px: 3 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 0.5, display: 'block', mb: 2, fontSize: '0.7rem' }}>TOP LOCATIONS</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {displayLocations.map((loc, idx) => {
                  const pct = Math.min(loc.percentage || 0, 100);
                  return (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography component="span" sx={{ fontSize: '1.25rem', lineHeight: 1, flexShrink: 0 }}>
                        {getFlagEmoji(!loc.country || String(loc.country).toLowerCase() === 'unknown' ? '' : (COUNTRY_TO_CODE[loc.country] || (loc.country?.length === 2 ? loc.country : 'XX')))}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, minWidth: 120 }} noWrap>{loc.city || 'Unknown'}</Typography>
                      <Box sx={{ flex: 1, minWidth: 0, height: 8, borderRadius: 1, bgcolor: isDark ? 'grey.700' : 'grey.200', overflow: 'hidden' }}>
                        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: '#00A7E3', borderRadius: 1, transition: 'width 0.2s ease' }} />
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, flexShrink: 0, minWidth: 44 }}>{loc.percentage}%</Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {!isFreePlan && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/analytics')} sx={{ textTransform: 'none', fontWeight: 600 }}>
            View full analytics
          </Button>
        </Box>
      )}
    </Container>
  );
};

const DashboardPaywall = ({ onUpgrade }) => (
  <Box sx={{
    position: 'absolute', inset: 0, zIndex: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <Box sx={{
      bgcolor: '#fff', borderRadius: 2, boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      maxWidth: 480, width: '100%', mx: 2, overflow: 'hidden', textAlign: 'center',
    }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${ACCENT} 0%, ${PRIMARY} 100%)`,
        px: 4, py: 3.5,
      }}>
        <Lock sx={{ fontSize: 36, color: 'rgba(255,255,255,0.9)', mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}>
          Upgrade to view analytics
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>
          We're tracking every view, visitor, and location for your cards. Unlock the full dashboard to see your performance.
        </Typography>
      </Box>

      <Box sx={{ px: 4, py: 3 }}>
        {[
          'Real-time view counter',
          'Location tracking (city & country)',
          'Device & source breakdown',
          'Daily & weekly trend charts',
          'FREE NFC visiting card included',
        ].map(f => (
          <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckCircleOutline sx={{ fontSize: 16, color: PRIMARY }} />
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>{f}</Typography>
          </Box>
        ))}

        <Button
          fullWidth variant="contained"
          onClick={onUpgrade}
          sx={{
            mt: 2.5, py: 1.25, bgcolor: PRIMARY, borderRadius: 1,
            fontWeight: 700, fontSize: '14px', textTransform: 'none',
            boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' },
          }}
        >
          View Plans — Starting ₹499/yr
        </Button>
        <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1, display: 'block' }}>
          Pro ₹499/yr · Business ₹999/yr · Lifetime Access ₹2499 one-time · + 18% GST
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default Dashboard;
